import type { Task } from '../types/project';

export const API_BASE = import.meta.env.VITE_API_URL ?? 'http://localhost:4000';

let csrfToken: string | null = null;

export async function getCsrfToken(): Promise<string> {
  if (csrfToken) return csrfToken;
  const res = await fetch(`${API_BASE}/auth/csrf`, { credentials: 'include' });
  if (!res.ok) throw new Error('Failed to get CSRF token');
  const data = await res.json();
  csrfToken = data.token;
  return csrfToken!;
}

async function request<T>(
  path: string,
  options: Omit<RequestInit, 'body'> & { method?: string; body?: unknown } = {}
): Promise<T> {
  const { method = 'GET', body, ...rest } = options;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(rest.headers as Record<string, string>),
  };
  if (method !== 'GET' && method !== 'HEAD') {
    headers['X-CSRF-Token'] = await getCsrfToken();
  }
  const fetchBody: BodyInit | undefined = body !== undefined ? JSON.stringify(body) : undefined;
  const res = await fetch(`${API_BASE}${path}`, {
    ...rest,
    method,
    credentials: 'include',
    headers,
    body: fetchBody,
  });
  if (res.status === 204) return undefined as T;
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error ?? res.statusText ?? 'Request failed');
  return data as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path, { method: 'GET' }),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body }),
  put:   <T>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body }),
  delete: (path: string) => request<void>(path, { method: 'DELETE' }),
};

export async function login(
  password: string,
  personId: string
): Promise<{ previousLoginAt: string | null }> {
  await getCsrfToken();
  return api.post<{ success: boolean; previousLoginAt: string | null }>('/auth/login', {
    password,
    personId,
  });
}

export async function logout(): Promise<void> {
  await api.post('/auth/logout');
  csrfToken = null;
}

export type MeResponse = {
  userId: string;
  personId: string | null;
  name: string | null;
  email: string | null;
};

export async function me(): Promise<MeResponse> {
  return api.get<MeResponse>('/api/me');
}

export async function fetchFoundersForLogin(): Promise<{ id: string; name: string }[]> {
  const res = await fetch(`${API_BASE}/auth/founders`, { credentials: 'include' });
  if (!res.ok) throw new Error('Failed to load founders');
  return res.json();
}

export async function fetchNotifications(): Promise<{
  notifications: Task[];
  previousLoginAt: string | null;
}> {
  return api.get('/api/notifications');
}
